import React, { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Server-driven pagination bar for the admin Bookings page.
 *
 * Renders:
 *   - Range text: "Showing 51-100 of 1,900"
 *   - Prev / [1] [2] ... [current] ... [last] / Next
 *   - Jump-to-page input (only shows when >5 pages so it doesn't
 *     clutter small result sets)
 *
 * All page changes are propagated through `onPageChange(nextPage)`; the
 * parent owns the URL/state sync.
 *
 * `variant`:
 *   - `default` (bottom of list) — with margin-top spacer.
 *   - `top` (beside heading) — no margin-top, tighter.
 */
export default function Pagination({ page, pageSize, total, onPageChange, variant = 'default' }) {
  const [jumpValue, setJumpValue] = useState('');
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  if (total === 0) return null;

  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  // Ellipsised page list: [1, ..., current-1, current, current+1, ..., last]
  // Keeps the bar to a bounded width regardless of total pages.
  const buildPageList = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages = new Set([1, totalPages, page, page - 1, page + 1]);
    if (page <= 3) [2, 3, 4].forEach(p => pages.add(p));
    if (page >= totalPages - 2) {
      [totalPages - 1, totalPages - 2, totalPages - 3].forEach(p => pages.add(p));
    }
    const sorted = [...pages].filter(p => p >= 1 && p <= totalPages).sort((a, b) => a - b);
    const out = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push('...');
      out.push(sorted[i]);
    }
    return out;
  };

  const handleJump = () => {
    const n = parseInt(jumpValue, 10);
    if (!Number.isFinite(n)) return;
    const clamped = Math.min(Math.max(1, n), totalPages);
    setJumpValue('');
    if (clamped !== page) onPageChange(clamped);
  };

  return (
    <div
      className={
        variant === 'top'
          ? 'flex flex-wrap items-center justify-between gap-3 py-2 px-3 bg-white border rounded-lg'
          : 'flex flex-wrap items-center justify-between gap-3 mt-6 py-3 px-4 bg-white border rounded-lg'
      }
      data-testid={variant === 'top' ? 'bookings-pagination-bar-top' : 'bookings-pagination-bar'}
    >
      <div className="text-sm text-muted-foreground" data-testid="pagination-range">
        Showing <span className="font-medium text-foreground">{start.toLocaleString()}</span>
        {'–'}
        <span className="font-medium text-foreground">{end.toLocaleString()}</span>
        {' of '}
        <span className="font-medium text-foreground">{total.toLocaleString()}</span>
      </div>

      <div className="flex items-center gap-1 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          data-testid="pagination-prev-btn"
        >
          <ChevronLeft className="w-4 h-4" />
          Prev
        </Button>

        {buildPageList().map((p, i) =>
          p === '...' ? (
            <span key={`ellipsis-${i}`} className="px-2 text-muted-foreground select-none">…</span>
          ) : (
            <Button
              key={p}
              variant={p === page ? 'default' : 'ghost'}
              size="sm"
              className={p === page ? 'font-bold min-w-9' : 'min-w-9'}
              onClick={() => onPageChange(p)}
              data-testid={`pagination-page-${p}`}
              aria-current={p === page ? 'page' : undefined}
            >
              {p}
            </Button>
          ),
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          data-testid="pagination-next-btn"
        >
          Next
          <ChevronRight className="w-4 h-4" />
        </Button>

        {totalPages > 5 && (
          <div className="flex items-center gap-1 ml-3 pl-3 border-l">
            <span className="text-xs text-muted-foreground">Go to</span>
            <Input
              type="number"
              min={1}
              max={totalPages}
              value={jumpValue}
              onChange={(e) => setJumpValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleJump(); }}
              placeholder={String(page)}
              className="w-16 h-8 text-sm"
              data-testid="pagination-jump-input"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={handleJump}
              disabled={!jumpValue}
              data-testid="pagination-jump-btn"
            >
              Go
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
